import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, Grid, Skeleton, Card } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Bar } from 'react-chartjs-2';
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import { chartColors, baseURL } from "../../utils/StaticVariables";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "./OrderFilter";

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { color: 'gray' } },
    title: { display: false }
  },
  scales: {
    x: { ticks: { color: 'gray' } },
    y: { ticks: { color: 'gray' } }
  }
};

const getHeatColor = (value, maxValue) => {
  if (value === 0) return { bg: 'rgba(0,0,0,0.05)', color: 'inherit' };

  const ratio = Math.min(value / maxValue, 1);

  const r = Math.round(100 - ratio * 255);
  const g = Math.round(200 - ratio * 40);
  const b = Math.round(255 - ratio * 50);
  const a = 0.3 + ratio * 1;
  const color = ratio > 0.5 ? '#fff' : '#000000';

  return { bg: `rgba(${r}, ${g}, ${b}, ${a})`, color };
};

const HotspotHeatmapChart = () => {
  const [hotspotData, setHotspotData] = useState(null);
  const [actionData, setActionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  // helper: بيحول الفلتر لـ params صح
  const buildParams = (f) => {
    const params = { ...f };
    if (params.locations) { params.location = params.locations; delete params.locations; }
    if (params.buildings) { params.building = params.buildings; delete params.buildings; }
    if (params.zones) { params.zone = params.zones; delete params.zones; }
    return params;
  };

  // Fetch heatmap data
  useEffect(() => {
    if (!token) return;
    const fetchHotspot = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${baseURL}shoplifting/dashboard/hotspots`, {
          headers: { Authorization: `Bearer ${token}` },
          params: buildParams(filter)
        });
        setHotspotData(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchHotspot();
  }, [filter, token]);

  // Fetch actions by camera/date
  useEffect(() => {
    if (!token) return;
    const fetchActions = async () => {
      setActionLoading(true);
      try {
        const { data } = await axios.get(`${baseURL}shoplifting/actions/by-camera-date`, {
          headers: { Authorization: `Bearer ${token}` },
          params: buildParams(filter)
        });
        // بيقبل array مباشرة أو { data: [...] }
        setActionData(Array.isArray(data) ? data : (data?.data || []));
      } catch (err) {
        console.error(err);
      } finally {
        setActionLoading(false);
      }
    };
    fetchActions();
  }, [filter, token]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  // ===== Heatmap logic =====
  const heatmap = hotspotData?.heatmap || [];

  const uniqueDays = Array.from(new Set(heatmap.map(h => h.date || h.day))).filter(Boolean).sort();
  const uniqueZones = Array.from(new Set(heatmap.map(h => h.zone || h.camera_zone || h.name))).filter(Boolean).sort();

  const maxIntensity = Math.max(
    ...heatmap.map(h => h.incident_count || h.count || h.incidents || 0),
    1
  );

  // ===== Event Type Breakdown logic (من الـ endpoint الجديد) =====
  // المحور X: camera_name | الـ datasets: action_taken
  const cameraLabels = Array.from(new Set(actionData.map(e => e.camera_name))).filter(Boolean);
  const actionTypes = Array.from(new Set(actionData.map(e => e.action_taken))).filter(Boolean);

  const actionDatasets = actionTypes.map((action, i) => {
    const values = cameraLabels.map(cam => {
      // لو في أكتر من تاريخ لنفس الكاميرا والأكشن، نجمعهم
      const matches = actionData.filter(e => e.camera_name === cam && e.action_taken === action);
      return matches.reduce((sum, e) => sum + (e.count || 0), 0);
    });
    return {
      label: action,
      data: values,
      backgroundColor: chartColors[i % chartColors.length]
    };
  });

  return (
    <Card sx={{ p: 2, height: 'auto', bgcolor: 'background.paper', mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Hotspot Heatmap</Typography>
        <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
        </DesBtn>
      </Box>

      {loading || !hotspotData ? (
        <Skeleton variant="rectangular" height="400px" sx={{ borderRadius: 2 }} />
      ) : (
        <Grid container spacing={3}>
          {/* ===== Heatmap ===== */}
          <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', height: '100%', overflowX: 'auto' }}>
              <Typography variant="subtitle1" mb={2}>Hotspot Risk Mapping</Typography>
              {uniqueDays.length === 0 && uniqueZones.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No heatmap data available for the selected filters.</Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `minmax(100px, max-content) repeat(${Math.max(uniqueDays.length, 1)}, minmax(40px, 1fr))`,
                  gap: 1,
                  minWidth: uniqueDays.length > 5 ? uniqueDays.length * 45 + 100 : '100%',
                }}>
                  {/* Header - Date columns */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">Zone / Date</Typography>
                  </Box>
                  {uniqueDays.map(d => (
                    <Box
                      key={d}
                      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60 }}
                    >
                      <Typography
                        fontWeight="bold"
                        noWrap
                        title={String(d)}
                        sx={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          fontSize: '11px',
                          lineHeight: 1,
                          textAlign: 'center',
                        }}
                      >
                        {String(d).substring(5, 10)}
                      </Typography>
                    </Box>
                  ))}

                  {/* Data rows */}
                  {uniqueZones.map((zone, zIdx) => (
                    <React.Fragment key={zone}>
                      <Typography
                        fontWeight="bold"
                        sx={{ alignSelf: 'center', pr: 1, fontSize: '13px' }}
                        noWrap
                        title={zone}
                      >
                        {zone.length > 14 ? zone.substring(0, 14) + '...' : zone}
                      </Typography>

                      {uniqueDays.map((day, dIdx) => {
                        const match = heatmap.find(h =>
                          (h.zone === zone || h.camera_zone === zone || h.name === zone) &&
                          (h.date === day || h.day === day)
                        );
                        const intensity = match
                          ? (match.incident_count || match.count || match.incidents || 0)
                          : 0;

                        const { bg, color } = getHeatColor(intensity, maxIntensity);

                        return (
                          <Box
                            key={`${zIdx}-${dIdx}`}
                            title={`${zone} — ${day}: ${intensity} incidents`}
                            sx={{
                              height: 44,
                              borderRadius: 1,
                              backgroundColor: bg,
                              color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '12px',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'scale(1.08)',
                                boxShadow: 3,
                                zIndex: 10
                              }
                            }}
                          >
                            {intensity || ''}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ===== Event Type Breakdown ===== */}
          <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', height: '100%' }}>
              <Typography variant="subtitle1" mb={2}>Event Type Breakdown</Typography>
              {actionLoading ? (
                <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
              ) : (
                <Box sx={{ height: 320 }}>
                  <Bar
                    data={{
                      labels: cameraLabels.length ? cameraLabels : ['No Data'],
                      datasets: actionDatasets.length
                        ? actionDatasets
                        : [{ label: 'No Actions', data: [0], backgroundColor: chartColors[0] }]
                    }}
                    options={{
                      ...commonOptions,
                      scales: {
                        ...commonOptions.scales,
                        x: { ...commonOptions.scales.x, stacked: true },
                        y: { ...commonOptions.scales.y, stacked: true }
                      }
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

export default HotspotHeatmapChart;